
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { Plus, Edit, Trash, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { GiftCatalog } from '@/types/giftCardTypes';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';

interface CatalogsListProps {
  adminId?: string;
}

const CatalogsList: React.FC<CatalogsListProps> = ({ adminId }) => {
  const { toast } = useToast();
  const [catalogs, setCatalogs] = useState<GiftCatalog[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCatalogId, setSelectedCatalogId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const fetchCatalogs = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke('tremendous', {
          body: {
            action: 'getCatalogsList',
            adminId: adminId || 'master-admin-1',
          },
        });

        if (error) {
          console.error('Error fetching catalogs:', error);
          toast({
            title: 'Error',
            description: 'Failed to fetch catalogs. Please try again.',
            variant: 'destructive',
          });
        }

        if (data) {
          setCatalogs(data);
        }
      } catch (error) {
        console.error('Unexpected error fetching catalogs:', error);
        toast({
          title: 'Unexpected Error',
          description: 'An unexpected error occurred while fetching catalogs.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCatalogs();
  }, [adminId, toast]);

  const handleCreateCatalog = () => {
    setIsCreating(true);
  };

  const handleEditCatalog = (catalogId: string) => {
    setSelectedCatalogId(catalogId);
    setIsEditing(true);
  };

  const handleDeleteCatalog = async (catalogId: string) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this catalog?');
    if (!confirmDelete) return;

    setLoading(true);
    try {
      // Instead of directly accessing the database, use the edge function
      const { data, error } = await supabase.functions.invoke('tremendous', {
        body: {
          action: 'deleteCatalog',
          catalogId: catalogId,
        },
      });

      if (error) {
        console.error('Error deleting catalog:', error);
        toast({
          title: 'Error',
          description: 'Failed to delete catalog. Please try again.',
          variant: 'destructive',
        });
      } else {
        setCatalogs(catalogs.filter((catalog) => catalog.id !== catalogId));
        toast({
          title: 'Success',
          description: 'Catalog deleted successfully.',
        });
      }
    } catch (error) {
      console.error('Unexpected error deleting catalog:', error);
      toast({
        title: 'Unexpected Error',
        description: 'An unexpected error occurred while deleting the catalog.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCatalogCreated = (newCatalog: GiftCatalog) => {
    setCatalogs([...catalogs, newCatalog]);
    setIsCreating(false);
  };

  const handleCatalogUpdated = (updatedCatalog: GiftCatalog) => {
    setCatalogs(
      catalogs.map((catalog) => (catalog.id === updatedCatalog.id ? updatedCatalog : catalog))
    );
    setIsEditing(false);
    setSelectedCatalogId(null);
  };

  const handleCloseModal = () => {
    setIsCreating(false);
    setIsEditing(false);
    setSelectedCatalogId(null);
  };

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Button onClick={handleCreateCatalog} disabled={isCreating || isEditing}>
          <Plus className="mr-2 h-4 w-4" />
          Create Catalog
        </Button>
      </div>

      {loading ? (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <CardTitle>
                  <Skeleton className="h-5 w-4/5" />
                </CardTitle>
                <CardDescription>
                  <Skeleton className="h-4 w-3/5" />
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Skeleton className="h-20" />
              </CardContent>
              <CardFooter className="flex justify-end">
                <Skeleton className="h-8 w-20" />
              </CardFooter>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {catalogs.map((catalog) => (
            <Card key={catalog.id}>
              <CardHeader>
                <CardTitle>{catalog.name}</CardTitle>
                <CardDescription>{catalog.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <p>Card Count: {catalog.cardCount}</p>
                <p>Created At: {new Date(catalog.createdAt).toLocaleDateString()}</p>
                {catalog.isDefault && (
                  <div className="mt-2 text-green-500 flex items-center">
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Default Catalog
                  </div>
                )}
              </CardContent>
              <CardFooter className="flex justify-end space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleEditCatalog(catalog.id)}
                  disabled={isCreating || isEditing}
                >
                  <Edit className="mr-2 h-4 w-4" />
                  Edit
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDeleteCatalog(catalog.id)}
                  disabled={isCreating || isEditing}
                >
                  <Trash className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      {isCreating && (
        <CatalogCreateDialog open={isCreating} onClose={handleCloseModal} onCatalogCreated={handleCatalogCreated} />
      )}

      {isEditing && selectedCatalogId && (
        <CatalogDetailView
          open={isEditing}
          onClose={handleCloseModal}
          catalogId={selectedCatalogId}
          onCatalogUpdated={handleCatalogUpdated}
        />
      )}
    </div>
  );
};

interface CatalogCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCatalogCreated: (catalog: GiftCatalog) => void;
}

const CatalogCreateDialog: React.FC<CatalogCreateDialogProps> = ({ open, onClose, onCatalogCreated }) => {
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    if (!name || !description) {
      toast({
        title: 'Error',
        description: 'Name and description are required.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('tremendous', {
        body: {
          action: 'createCatalog',
          name: name,
          description: description,
          adminId: 'master-admin-1',
        },
      });

      if (error) {
        console.error('Error creating catalog:', error);
        toast({
          title: 'Error',
          description: 'Failed to create catalog. Please try again.',
          variant: 'destructive',
        });
      }

      if (data) {
        onCatalogCreated(data);
        toast({
          title: 'Success',
          description: 'Catalog created successfully.',
        });
        onClose();
      }
    } catch (error) {
      console.error('Unexpected error creating catalog:', error);
      toast({
        title: 'Unexpected Error',
        description: 'An unexpected error occurred while creating the catalog.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Catalog</DialogTitle>
          <DialogDescription>Create a new catalog to organize your gift cards.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="name" className="text-right">
              Name
            </Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} className="col-span-3" />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="description" className="text-right">
              Description
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="col-span-3"
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" onClick={handleCreate} disabled={loading}>
            {loading ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface CatalogDetailViewProps {
  open: boolean;
  onClose: () => void;
  catalogId: string;
  onCatalogUpdated: (catalog: GiftCatalog) => void;
}

const CatalogDetailView: React.FC<CatalogDetailViewProps> = ({ open, onClose, catalogId, onCatalogUpdated }) => {
  const { toast } = useToast();
  const [catalog, setCatalog] = useState<GiftCatalog | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchCatalogDetails = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke('tremendous', {
          body: {
            action: 'getCatalogDetails',
            catalogId: catalogId,
          },
        });

        if (error) {
          console.error('Error fetching catalog details:', error);
          toast({
            title: 'Error',
            description: 'Failed to fetch catalog details. Please try again.',
            variant: 'destructive',
          });
        }

        if (data) {
          setCatalog(data);
          setName(data.name);
          setDescription(data.description);
          setIsDefault(data.isDefault);
        }
      } catch (error) {
        console.error('Unexpected error fetching catalog details:', error);
        toast({
          title: 'Unexpected Error',
          description: 'An unexpected error occurred while fetching catalog details.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCatalogDetails();
  }, [catalogId, toast]);

  const handleSave = async () => {
    if (!name || !description) {
      toast({
        title: 'Error',
        description: 'Name and description are required.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke('tremendous', {
        body: {
          action: 'updateCatalog',
          catalogId: catalogId,
          name: name,
          description: description,
          isDefault: isDefault,
        },
      });

      if (error) {
        console.error('Error updating catalog:', error);
        toast({
          title: 'Error',
          description: 'Failed to update catalog. Please try again.',
          variant: 'destructive',
        });
      }

      if (data) {
        onCatalogUpdated(data);
        toast({
          title: 'Success',
          description: 'Catalog updated successfully.',
        });
        onClose();
      }
    } catch (error) {
      console.error('Unexpected error updating catalog:', error);
      toast({
        title: 'Unexpected Error',
        description: 'An unexpected error occurred while updating the catalog.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Catalog</DialogTitle>
          <DialogDescription>Edit the details of the selected catalog.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-4">
            <Skeleton className="h-8 w-full mb-2" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : (
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Name
              </Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} className="col-span-3" />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="description" className="text-right">
                Description
              </Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <div className="col-span-4 flex items-center space-x-2">
                <Switch id="isDefault" checked={isDefault} onCheckedChange={setIsDefault} />
                <Label htmlFor="isDefault">Set as Default</Label>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CatalogsList;
